/**
 * WEEKS ACTUALLY WORKED — one derivation of a Role's working year, for both ends.
 *
 * ══════════════════════════════════════════════════════════════════════════
 * WHY THIS FILE EXISTS
 * ══════════════════════════════════════════════════════════════════════════
 *
 * A Role held two stored answers to "how many weeks does this Role work in a
 * year" and one place that could compute one. `RoleWorkingYear` stores four
 * terms; `deriveJobModel` turned them into a figure IN A BROWSER; and the
 * `weeksPerYear` magnitude frozen inside `RoleWorkload.formula` — the number
 * the coverage denominator is actually built from — was seeded from the
 * ORGANISATION's working year instead. `RoleGeneralSection` draws both on one
 * screen, so 43.6 and 46 sat two rows apart with nothing reconciling them.
 *
 * This is the arithmetic, once, in a package both ends already depend on. The
 * server publishes it on the working-year read so a non-browser client gets the
 * same figure; the browser calls it directly so a half-typed form still
 * recomputes as the reader types. Neither end owns a second copy of the rule.
 *
 * ══════════════════════════════════════════════════════════════════════════
 * 🚨 THIS IS NOT A COVERAGE INPUT, AND KEEPING IT OUT OF ONE IS DELIBERATE
 * ══════════════════════════════════════════════════════════════════════════
 *
 * `role-coverage-inputs-are-the-documented-three.spec.ts` pins the claim that
 * `GetRoleCoverageUseCase` reads `OrganizationAutomationSettings`,
 * `RoleWorkload` and `RoleSystemImpact` and nothing else — a claim four
 * documents also make. Nothing here is reachable from that use case: this
 * module imports no coverage type, and the working-year read is a separate
 * route with a separate port.
 *
 * That is why {@link WORKED_DAYS_PER_WEEK} is a constant and NOT the
 * organisation's `daysPerWeek`. Reading the organisation's working-time model
 * here would look like an improvement — the divisor is, after all, "days in a
 * working week" — and it would quietly make a Role's working year depend on a
 * coverage input, which is the first step of the merge those documents exist to
 * refuse. The reference model this screen ports divides by five, and five is
 * what it divides by.
 *
 * ══════════════════════════════════════════════════════════════════════════
 * THE `null` IS THE POINT, AND IT IS NOT AN ERROR STATE
 * ══════════════════════════════════════════════════════════════════════════
 *
 * Four unset terms parse to zero, and a floor at one week would lift that to
 * "Weeks actually worked ~ 1.0" for a Role nobody has modelled — a figure
 * invented out of four blanks. So the CALENDAR YEAR decides: it is the subject
 * of the whole calculation and the other three are subtractions from it, so
 * subtracting leave from a year nobody wrote down produces a number with no
 * referent.
 *
 * The other three are different: `null` there is "not stated", which subtracts
 * nothing, and that is a real answer rather than a gap. A Role that states a
 * 52-week year and nothing else works 52 weeks, and says so.
 *
 * With the year stated, the floor is back and means what the reference meant: a
 * guard against a calendar whose leave exceeds it.
 */

/**
 * The divisor that turns days of leave into weeks of leave.
 *
 * A constant on purpose — see the header. Making this the organisation's
 * `daysPerWeek` would make a Role's working year a function of a coverage
 * input.
 */
export const WORKED_DAYS_PER_WEEK = 5;

/** The fewest weeks a stated working year may come out at. */
export const MINIMUM_WEEKS_WORKED = 1;

/**
 * The four stored terms, as `RoleWorkingYear` holds them.
 *
 * `null` is "not stated" in every field, and it is read differently for the
 * calendar year than for the three subtractions — see the header.
 */
export interface RoleWorkingYearTerms {
  /** Where every contract starts, in weeks. Typically 52. */
  readonly calendarWeeks: number | null;
  /** Statutory and contractual paid leave, in weeks. */
  readonly paidLeaveWeeks: number | null;
  /** Worked days lost to public holidays in a normal year. */
  readonly publicHolidayDays: number | null;
  /** Sickness and training, in days, as a rolling average across the team. */
  readonly sicknessDays: number | null;
}

/**
 * Weeks actually worked, or `null` when this Role has not stated its calendar
 * year.
 *
 * Pure, and total over its input type: every branch returns, no throw, and the
 * only `null` it produces is the stated-calendar one. A caller holding a value
 * it could not READ — a half-typed form field that is not a number — must
 * answer `null` itself before calling, because an unreadable term and an unset
 * one are different answers and this function only knows about the second.
 */
export function deriveRoleWorkingYearWeeks(terms: RoleWorkingYearTerms): number | null {
  if (terms.calendarWeeks === null) {
    return null;
  }

  const worked =
    terms.calendarWeeks -
    (terms.paidLeaveWeeks ?? 0) -
    (terms.publicHolidayDays ?? 0) / WORKED_DAYS_PER_WEEK -
    (terms.sicknessDays ?? 0) / WORKED_DAYS_PER_WEEK;

  return Math.max(MINIMUM_WEEKS_WORKED, worked);
}
